// components
import FilePreviewLayout from '../preview/FilePreviewLayout';
// services
import useArtifactService from '../../../artifactService';
import { useAsyncValue } from 'framework/utils';
import { InputLoader } from 'framework/ui/mui-builder';
import { DocumentStorageItemType } from 'framework/documentStorage';
import { getFileTypeFromName } from '../../../utils';
import { useDocumentStorageContext } from 'framework/documentStorage';
import { Box } from '@mui/material';

/**
 * Artifact
 * @param artifactsIdsList - list of artifacts id's
 * @param onRemove(id) - function that removes artifact from the context
 * @constructor
 */

type FilePreviewListLayoutProps = {
  artifactsIdsList: string[];
  onRemove: (id: string) => void;
  disabled?: boolean;
  isNewDocumentStorage?: boolean;
  isCandidateService?: boolean;
  isProjectService?: boolean;
  isRiskService?: boolean;
  isStaffingService?: boolean;
  budgetId?: StringOrNumber;
};

type PreviewArtifact = {
  id: string;
  title: string;
  name: string;
  type: string;
  path?: string;
  [key: string]: unknown;
};

const mapDocumentToPreviewArtifact = (document: DocumentStorageItemType): PreviewArtifact => {
  const fileName = document.metadata?.fileName || 'Document';

  return {
    ...document,
    type: getFileTypeFromName(fileName),
    name: fileName,
    title: fileName,
  };
};

const FilePreviewListLayout = ({
  artifactsIdsList,
  onRemove,
  disabled = false,
  isNewDocumentStorage,
  isCandidateService = false,
  isProjectService = false,
  isRiskService = false,
  isStaffingService = false,
  budgetId,
}: FilePreviewListLayoutProps) => {
  const { getArtifactsDataByIds, isArtifactsInfoReceiverLoading } = useArtifactService({
    isCandidateService,
    isProjectService,
    isRiskService,
    isStaffingService,
    budgetId,
  });
  const { getDocumentByPaths } = useDocumentStorageContext({ ignoreContext: !isNewDocumentStorage });

  const artifactsObjects = useAsyncValue<Nullable<PreviewArtifact[]>>(async () => {
    if (isNewDocumentStorage) {
      const documents = await getDocumentByPaths(artifactsIdsList);
      return documents.map(mapDocumentToPreviewArtifact);
    }

    if (isArtifactsInfoReceiverLoading) {
      return null;
    }

    return getArtifactsDataByIds(artifactsIdsList) as Promise<PreviewArtifact[]>;
  }, [artifactsIdsList, isNewDocumentStorage, isArtifactsInfoReceiverLoading, budgetId]);

  if (!artifactsIdsList?.length) {
    return null;
  }

  return artifactsObjects ? (
    artifactsObjects.map((artifactObject: Dictionary<any>) => {
      return (
        <FilePreviewLayout
          isNewDocumentStorage={isNewDocumentStorage}
          key={artifactObject.id}
          onRemove={() => onRemove(artifactObject.id)}
          artifactObject={artifactObject}
          disabled={disabled}
          budgetId={budgetId}
        />
      );
    })
  ) : (
    <Box
      position="relative"
      sx={{
        '& i': {
          position: 'absolute',
          fontSize: '14px',
          left: 'calc(50% - 7px)',
          top: 'calc(50% - 7px)',
        },
      }}
    >
      <InputLoader />
    </Box>
  );
};

export default FilePreviewListLayout;
